import React, { useEffect } from 'react'
import { useTranslate, useInput } from 'react-admin'

import { Box, Chip, FormControl, InputLabel, Select, SelectChangeEvent, SelectProps } from '@mui/material'
import { FilterIcon } from 'common/Icons'

import 'styles.scss'

interface Choice {
  id: string,
  [key: string]: any
}

interface CustomArrayInputProps {
  source: string,
  choices: Choice[]
  label: string,
  alwaysOn?: boolean,
  fieldProps?: SelectProps<string[]>,
  onChange?: (value: string[]) => void,
  defaultValue?: string[],
  fullWidth?: boolean,
  getOptionLabel?: (choice: Choice) => string
}

export const OutlinedArrayInput = (props: CustomArrayInputProps) => {
  const { source, choices } = props
  const { field } = useInput({ source: source })

  // Convert string[] to Choice[] for CustomMultiselect
  const defaultValueObjects = field.value
    ? (field.value as string[]).map(id => choices.find(c => c.id === id)).filter((c): c is Choice => c !== undefined)
    : []

  // Convert Choice[] back to string[] for react-admin
  const handleChange = (selectedChoices: Choice[]) => {
    field.onChange(selectedChoices.map(c => c.id))
  }

  return <CustomMultiselect
    {...props}
    defaultValue={defaultValueObjects}
    onChange={handleChange}
  />
}

interface CustomMultiselectProps<T extends Choice = Choice> {
  choices: T[]
  label: string,
  alwaysOn?: boolean,
  fieldProps?: SelectProps<string[]>,
  onChange?: (value: T[]) => void,
  defaultValue?: T[],
  fullWidth?: boolean
  skipTranslate?: boolean
  dynamicHeight?: boolean
  getOptionLabel?: (choice: T) => string
}

export const CustomMultiselect = <T extends Choice = Choice>({ choices, label, fieldProps, onChange, fullWidth, defaultValue, skipTranslate = false, dynamicHeight = false, getOptionLabel }: CustomMultiselectProps<T>) => {
  const translate = useTranslate()
  const [selectedItems, setSelectedItems] = React.useState<string[]>(
    defaultValue?.map(item => item.id) || []
  )
  const selectRef = React.useRef<HTMLDivElement>(null)
  const [menuWidth, setMenuWidth] = React.useState<number>(0)
  const [open, setOpen] = React.useState(false)

  const getLabel = (choice: T | undefined) => {
    if (!choice) return ''
    if (getOptionLabel) return getOptionLabel(choice)
    return (choice as any).name || ''
  }

  useEffect(() => {
    if (onChange) {
      const selectedObjects = selectedItems
        .map(id => choices.find(c => c.id === id))
        .filter((item): item is T => item !== undefined)
      onChange(selectedObjects)
    }
  }, [selectedItems])

  useEffect(() => {
    if (selectRef.current) {
      setMenuWidth(selectRef.current.offsetWidth)
    }
  }, [selectRef.current])

  const handleItemSelect = (event: SelectChangeEvent<string[]>) => {
    setSelectedItems(event?.target?.value as string[])
  }

  const chipRemove = (item: string) => {
    const updatedSelectedItems = selectedItems.filter((selectedItem) => selectedItem !== item)
    setSelectedItems(updatedSelectedItems)
  }

  const chipAdd = (item: string) => {
    const updatedSelectedItems = [...new Set([...selectedItems, item])]
    setSelectedItems(updatedSelectedItems)

    // Check if all items are now selected
    const remainingChoices = choices.filter(choice => !updatedSelectedItems.includes(choice.id))
    if (remainingChoices.length === 0) {
      // Use setTimeout to close after state updates
      setTimeout(() => setOpen(false), 0)
    }
  }

  return (<>
    <FormControl ref={selectRef} className={`custom-array-input margin ${dynamicHeight ? 'dynamic-height' : ''}`} sx={fullWidth ? { minWidth: "100% !important" } : undefined}>
      <InputLabel id="profile" shrink>{label}</InputLabel>
      <Select
        {...fieldProps}
        multiple
        notched
        label={label}
        value={selectedItems}
        open={open}
        onOpen={() => {
          // Only open if there are items left to select
          const remainingChoices = choices.filter(choice => !selectedItems.includes(choice.id))
          if (remainingChoices.length > 0) {
            setOpen(true)
          }
        }}
        onClose={() => setOpen(false)}
        onChange={handleItemSelect}
        sx={{
          '& .MuiSelect-select': {
            cursor: choices.length === selectedItems.length ? 'default' : 'pointer'
          }
        }}
        inputProps={{
          ...(choices.length - selectedItems.length == 0 ? { IconComponent: () => null } : {})
        }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((item) => (
              <Chip
                key={item}
                className={`big-chip ${skipTranslate ? 'no-uppercase' : ''}`}
                label={skipTranslate ? getLabel(choices.find(c => c?.id == item)) : translate(getLabel(choices.find(c => c?.id == item)))}
                onDelete={() => { chipRemove(item) }}
                deleteIcon={
                  <FilterIcon
                    type="gray-x"
                    onMouseDown={(event) => event.stopPropagation()} />
                }
              />
            ))}
          </Box>
        )}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            sx: {
              ".Mui-selected": {
                display: "none"
              },
              width: menuWidth > 0 ? `${menuWidth}px` : 'auto',
              boxShadow: '0px 3px 6px #00000029',
              border: '1px solid #CCCCCC',
              marginTop: '8px'
            }
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left"
          }
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, padding: '8px', width: '100%', boxSizing: 'border-box' }}>
          {choices
            .filter(choice => !selectedItems.includes(choice.id))
            .map((choice) => (
              <Chip
                key={choice.id}
                className={`squere-chip ${skipTranslate ? 'no-uppercase' : ''}`}
                label={skipTranslate ? getLabel(choice) : translate(getLabel(choice))}
                onClick={() => {
                  chipAdd(choice.id)
                }}
                onDelete={() => chipAdd(choice.id)}
                deleteIcon={
                  <FilterIcon
                    type="gray-plus"
                  />
                }
              />
            ))}
        </Box>
      </Select>
    </FormControl>
  </>

  )
}